import { useState, useEffect } from 'preact/hooks';
import { getLeaveTypes, getLocations, getDepartments, getHolidays, getAllUsers, createDepartment, updateDepartment, deleteDepartment, createLeaveType, updateLeaveType, deleteLeaveType, createLocation, createHoliday, deleteHoliday, getNotificationSettings, updateNotificationSettings, getSlackChannels, getWorkspaceSettings, updateWorkspaceSettings, getDataStats, downloadBackup, clearNonAdminUsers, clearLeaveHistory, getAllUserBalances, updateUserBalance, getDepartmentPolicies, updateDepartmentPolicies } from '../lib/api';

export function SettingsPage({ user, workspace }) {
  const [activeTab, setActiveTab] = useState('departments');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const [typesData, locsData, deptsData, holidaysData, usersData] = await Promise.all([
        getLeaveTypes(),
        getLocations(),
        getDepartments(),
        getHolidays(new Date().getFullYear()),
        getAllUsers()
      ]);
      setLeaveTypes(typesData || []);
      setLocations(locsData || []);
      setDepartments(deptsData || []);
      setHolidays(holidaysData || []);
      setAllUsers(usersData || []);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }

  if (!isAdmin) {
    return (
      <div class="empty-state" style="margin-top: 3rem;">
        <div class="empty-state-icon">🔒</div>
        <h3>Admin Access Required</h3>
        <p>You need admin permissions to access settings.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'departments', label: 'Departments', icon: '🏢' },
    { id: 'locations', label: 'Locations', icon: '📍' },
    { id: 'holidays', label: 'Holidays', icon: '🎉' },
    { id: 'balances', label: 'Opening Balances', icon: '📊' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'workspace', label: 'Workspace', icon: '⚙️' },
    ...(user?.role === 'owner' ? [{ id: 'data', label: 'Data Management', icon: '🗄️' }] : [])
  ];

  return (
    <div class="settings-page">
      <div class="page-header">
        <div class="page-header-content">
          <h1>Settings</h1>
          <p class="page-subtitle">Configure your workspace settings and policies</p>
        </div>
      </div>

      {/* Tabs */}
      <div class="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            class={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span class="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div class="loading-screen" style="min-height: 300px;">
          <div class="spinner" />
        </div>
      ) : (
        <>
          {activeTab === 'departments' && (
            <DepartmentsTab departments={departments} allUsers={allUsers} leaveTypes={leaveTypes} onRefresh={loadSettings} />
          )}
          {activeTab === 'locations' && (
            <LocationsTab locations={locations} onRefresh={loadSettings} />
          )}
          {activeTab === 'holidays' && (
            <HolidaysTab holidays={holidays} locations={locations} onRefresh={loadSettings} />
          )}
          {activeTab === 'balances' && (
            <OpeningBalancesTab allUsers={allUsers} leaveTypes={leaveTypes} />
          )}
          {activeTab === 'notifications' && (
            <NotificationsTab departments={departments} />
          )}
          {activeTab === 'workspace' && (
            <WorkspaceTab workspace={workspace} />
          )}
          {activeTab === 'data' && user?.role === 'owner' && (
            <DataManagementTab />
          )}
        </>
      )}
    </div>
  );
}

function DepartmentsTab({ departments, allUsers, leaveTypes, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_default: false,
    approver_ids: [],
    working_days: [1, 2, 3, 4, 5], // Mon-Fri default
    hours_per_day: 8
  });
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [saving, setSaving] = useState(false);

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Leave type management state
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [leaveTypeForm, setLeaveTypeForm] = useState({
    name: '',
    icon: '🏖️',
    color: '#3B82F6',
    unit: 'days',
    requires_approval: true,
    allows_half_day: true,
    is_paid: true
  });

  function openAddLeaveTypeModal() {
    setEditingLeaveType(null);
    setLeaveTypeForm({
      name: '',
      icon: '🏖️',
      color: '#3B82F6',
      unit: 'days',
      requires_approval: true,
      allows_half_day: true,
      is_paid: true
    });
    setShowLeaveTypeModal(true);
  }

  function openEditLeaveTypeModal(policy) {
    const lt = leaveTypes.find(t => t.id === policy.leave_type_id);
    if (!lt) return;
    setEditingLeaveType(lt);
    setLeaveTypeForm({
      name: lt.name,
      icon: lt.icon || '🏖️',
      color: lt.color || '#3B82F6',
      unit: lt.unit || 'days',
      requires_approval: lt.requires_approval ?? true,
      allows_half_day: lt.allows_half_day ?? true,
      is_paid: lt.is_paid ?? true
    });
    setShowLeaveTypeModal(true);
  }

  async function handleLeaveTypeSubmit(e) {
    e.preventDefault();
    try {
      if (editingLeaveType) {
        await updateLeaveType(editingLeaveType.id, leaveTypeForm);
      } else {
        await createLeaveType({ ...leaveTypeForm, default_entitlement: 25 });
      }
      setShowLeaveTypeModal(false);
      setEditingLeaveType(null);
      onRefresh();
    } catch (err) {
      alert(`Failed to ${editingLeaveType ? 'update' : 'create'} leave type`);
    }
  }

  async function handleDeleteLeaveType(policy) {
    const lt = leaveTypes.find(t => t.id === policy.leave_type_id);
    if (!lt) return;
    if (!confirm(`Delete "${lt.name}"? This will remove it from all departments.`)) return;
    try {
      await deleteLeaveType(lt.id);
      onRefresh();
    } catch (err) {
      alert('Failed to delete. It may be in use by leave requests.');
    }
  }

  function getDepartmentMembers(deptId) {
    return allUsers.filter(u => u.department_id === deptId && u.is_active);
  }

  function getDepartmentApprovers(dept) {
    // Get approvers from the stored approver_ids array
    const approverIds = dept.approver_ids || [];
    return allUsers.filter(u => approverIds.includes(u.id));
  }

  async function openEditModal(dept) {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      is_default: dept.is_default || false,
      approver_ids: dept.approver_ids || [],
      working_days: dept.working_days || [1, 2, 3, 4, 5],
      hours_per_day: dept.hours_per_day ?? 8
    });
    setShowModal(true);
    // Load policies for this department
    setLoadingPolicies(true);
    try {
      const deptPolicies = await getDepartmentPolicies(dept.id);
      setPolicies(deptPolicies || []);
    } catch (err) {
      console.error('Failed to load department policies:', err);
      // Initialize with leave types defaults
      setPolicies((leaveTypes || []).map(lt => ({
        leave_type_id: lt.id,
        name: lt.name,
        icon: lt.icon,
        color: lt.color,
        unit: lt.unit,
        default_entitlement: lt.default_entitlement,
        entitlement: lt.default_entitlement,
        is_enabled: true,
        has_custom_policy: false
      })));
    } finally {
      setLoadingPolicies(false);
    }
  }

  function openAddModal() {
    setEditingDept(null);
    setFormData({
      name: '',
      is_default: false,
      approver_ids: [],
      working_days: [1, 2, 3, 4, 5],
      hours_per_day: 8
    });
    // Initialize policies with leave type defaults
    setPolicies((leaveTypes || []).map(lt => ({
      leave_type_id: lt.id,
      name: lt.name,
      icon: lt.icon,
      color: lt.color,
      unit: lt.unit,
      default_entitlement: lt.default_entitlement,
      entitlement: lt.default_entitlement,
      is_enabled: true,
      has_custom_policy: false
    })));
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let deptId;
      if (editingDept) {
        await updateDepartment(editingDept.id, formData);
        deptId = editingDept.id;
      } else {
        const result = await createDepartment(formData);
        deptId = result.id;
      }
      // Save leave policies
      if (deptId && policies.length > 0) {
        await updateDepartmentPolicies(deptId, policies.map(p => ({
          leave_type_id: p.leave_type_id,
          entitlement: p.entitlement,
          is_enabled: p.is_enabled
        })));

        // Update leave type units if changed
        for (const policy of policies) {
          const originalLeaveType = leaveTypes.find(lt => lt.id === policy.leave_type_id);
          if (originalLeaveType && originalLeaveType.unit !== policy.unit) {
            await updateLeaveType(policy.leave_type_id, { unit: policy.unit });
          }
        }
      }
      setShowModal(false);
      onRefresh();
    } catch (err) {
      alert('Failed to save department');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(deptId) {
    if (!confirm('Are you sure you want to delete this department? Users will be unassigned.')) return;
    try {
      await deleteDepartment(deptId);
      onRefresh();
    } catch (err) {
      alert('Failed to delete department');
    }
  }

  async function handleSetDefault(deptId) {
    try {
      await updateDepartment(deptId, { is_default: true });
      onRefresh();
    } catch (err) {
      alert('Failed to set default department');
    }
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Departments</h2>
          <p>Configure departments with their leave types, entitlements, and approvers</p>
        </div>
        <button class="btn btn-primary" onClick={openAddModal}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Department
        </button>
      </div>

      <div class="departments-grid">
        {departments.length > 0 ? (
          departments.map(dept => {
            const members = getDepartmentMembers(dept.id);
            const approvers = getDepartmentApprovers(dept);
            const displayMembers = members.slice(0, 5);
            const moreCount = members.length - 5;

            return (
              <div key={dept.id} class={`department-card ${dept.is_default ? 'default' : ''}`}>
                <div class="department-card-header">
                  <div class="department-icon">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div class="department-title">
                    <h3>{dept.name}</h3>
                    {dept.is_default && <span class="badge badge-approved">Default</span>}
                  </div>
                  <div class="department-menu">
                    <button class="btn-icon" onClick={() => openEditModal(dept)}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div class="department-stats">
                  <div class="stat-item">
                    <span class="stat-value">{members.length}</span>
                    <span class="stat-label">Members</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-value">{approvers.length}</span>
                    <span class="stat-label">Approvers</span>
                  </div>
                </div>

                {members.length > 0 && (
                  <div class="department-members">
                    <span class="members-label">Team Members</span>
                    <div class="avatar-stack">
                      {displayMembers.map(member => (
                        <div key={member.id} class="avatar avatar-sm" title={member.name}>
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt="" />
                          ) : (
                            member.name?.charAt(0)
                          )}
                        </div>
                      ))}
                      {moreCount > 0 && (
                        <div class="avatar avatar-sm more-count">+{moreCount}</div>
                      )}
                    </div>
                  </div>
                )}

                {approvers.length > 0 && (
                  <div class="department-approvers">
                    <span class="approvers-label">Approvers</span>
                    <div class="approvers-list">
                      {approvers.map(approver => (
                        <div key={approver.id} class="approver-chip">
                          <div class="avatar avatar-xs">
                            {approver.avatar_url ? (
                              <img src={approver.avatar_url} alt="" />
                            ) : (
                              approver.name?.charAt(0)
                            )}
                          </div>
                          <span>{approver.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div class="department-actions">
                  <button class="btn btn-sm btn-primary" onClick={() => openEditModal(dept)}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginRight: '0.25rem' }}>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Leave Policies
                  </button>
                  {!dept.is_default && (
                    <button class="btn btn-sm btn-secondary" onClick={() => handleSetDefault(dept.id)}>
                      Set as Default
                    </button>
                  )}
                  <button class="btn btn-sm btn-danger-outline" onClick={() => handleDelete(dept.id)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div class="empty-card full-width">
            <div class="empty-state-icon">🏢</div>
            <h3>No departments yet</h3>
            <p>Create departments to organize your team</p>
            <button class="btn btn-primary" onClick={openAddModal}>Add First Department</button>
          </div>
        )}
      </div>

      {/* Department Modal */}
      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
              <div class="modal-header" style={{ flexShrink: 0 }}>
                <h2>{editingDept ? 'Edit Department' : 'Add Department'}</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div class="form-group">
                  <label>Department Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Engineering, Marketing"
                  />
                </div>

                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    Set as Default Department
                  </label>
                  <p class="form-hint">New users will be automatically assigned to this department</p>
                </div>

                {/* Working Days Configuration */}
                <div class="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                  <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Working Days</label>
                  <p class="form-hint" style={{ marginBottom: '0.75rem' }}>Select which days of the week are working days for this department</p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {dayNames.map((day, idx) => (
                      <label
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '48px',
                          height: '40px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          border: '2px solid',
                          borderColor: formData.working_days.includes(idx) ? '#3b82f6' : '#e5e7eb',
                          background: formData.working_days.includes(idx) ? '#eff6ff' : 'white',
                          color: formData.working_days.includes(idx) ? '#1e40af' : '#6b7280',
                          transition: 'all 0.15s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.working_days.includes(idx)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, working_days: [...formData.working_days, idx].sort() });
                            } else {
                              setFormData({ ...formData, working_days: formData.working_days.filter(d => d !== idx) });
                            }
                          }}
                          style={{ display: 'none' }}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Hours per Day */}
                <div class="form-group">
                  <label>Hours per Working Day</label>
                  <p class="form-hint">Used for converting between hours and days</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="number"
                      class="form-input"
                      value={formData.hours_per_day}
                      onChange={(e) => setFormData({ ...formData, hours_per_day: parseFloat(e.target.value) || 8 })}
                      min="1"
                      max="24"
                      step="0.5"
                      style={{ width: '100px' }}
                    />
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>hours</span>
                  </div>
                </div>

                <div class="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                  <label>Approvers</label>
                  <p class="form-hint">Select users who can approve leave requests for this department</p>
                  <div class="approver-selector">
                    {allUsers.filter(u => u.is_active && (u.role === 'approver' || u.role === 'admin' || u.role === 'owner')).map(u => (
                      <label key={u.id} class="approver-option">
                        <input
                          type="checkbox"
                          checked={formData.approver_ids.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, approver_ids: [...formData.approver_ids, u.id] });
                            } else {
                              setFormData({ ...formData, approver_ids: formData.approver_ids.filter(id => id !== u.id) });
                            }
                          }}
                        />
                        <div class="avatar avatar-sm">
                          {u.avatar_url ? <img src={u.avatar_url} alt="" /> : u.name?.charAt(0)}
                        </div>
                        <span>{u.name}</span>
                        <span class="role-badge">{u.role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Leave Policies Section */}
                <div class="form-group" style={{
                  marginTop: '1.5rem',
                  paddingTop: '1.5rem',
                  borderTop: '2px solid #e5e7eb'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <svg width="20" height="20" fill="none" stroke="#3b82f6" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <label style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#1e40af' }}>Leave Types & Entitlements</label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm btn-secondary"
                      onClick={openAddLeaveTypeModal}
                      style={{ fontSize: '0.75rem' }}
                    >
                      + Add Leave Type
                    </button>
                  </div>
                  <p class="form-hint" style={{ marginBottom: '1rem' }}>
                    Configure leave types and set entitlements for this department.
                  </p>
                  {loadingPolicies ? (
                    <div style="padding: 1rem; text-align: center;">
                      <div class="spinner" style="width: 20px; height: 20px;" />
                    </div>
                  ) : policies.length > 0 ? (
                    <div class="leave-policies-list" style={{
                      background: '#f8fafc',
                      borderRadius: '8px',
                      padding: '0.5rem'
                    }}>
                      {policies.map((policy, idx) => (
                        <div key={policy.leave_type_id} class={`leave-policy-row ${!policy.is_enabled ? 'disabled' : ''}`}>
                          <label class="policy-toggle">
                            <input
                              type="checkbox"
                              checked={policy.is_enabled}
                              onChange={(e) => {
                                const newPolicies = [...policies];
                                newPolicies[idx] = { ...policy, is_enabled: e.target.checked };
                                setPolicies(newPolicies);
                              }}
                            />
                            <span class="policy-name">
                              <span class="policy-icon">{policy.icon}</span>
                              {policy.name}
                            </span>
                          </label>
                          <div class="policy-entitlement">
                            <label class="unlimited-toggle" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginRight: '0.5rem' }}>
                              <input
                                type="checkbox"
                                checked={policy.entitlement === -1}
                                disabled={!policy.is_enabled}
                                onChange={(e) => {
                                  const newPolicies = [...policies];
                                  newPolicies[idx] = { ...policy, entitlement: e.target.checked ? -1 : policy.default_entitlement || 25 };
                                  setPolicies(newPolicies);
                                }}
                                style={{ width: '14px', height: '14px' }}
                              />
                              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Unlimited</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step={policy.unit === 'hours' ? '1' : '0.5'}
                              value={policy.entitlement === -1 ? '' : policy.entitlement}
                              disabled={!policy.is_enabled || policy.entitlement === -1}
                              placeholder={policy.entitlement === -1 ? '∞' : ''}
                              onChange={(e) => {
                                const newPolicies = [...policies];
                                newPolicies[idx] = { ...policy, entitlement: parseFloat(e.target.value) || 0 };
                                setPolicies(newPolicies);
                              }}
                              class="form-input entitlement-input"
                              style={{ opacity: policy.entitlement === -1 ? 0.5 : 1 }}
                            />
                            <select
                              class="form-select entitlement-unit-select"
                              value={policy.unit || 'days'}
                              disabled={!policy.is_enabled}
                              onChange={(e) => {
                                const newPolicies = [...policies];
                                newPolicies[idx] = { ...policy, unit: e.target.value };
                                setPolicies(newPolicies);
                              }}
                              style={{
                                width: 'auto',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.875rem',
                                minWidth: '70px'
                              }}
                            >
                              <option value="hours">hours</option>
                              <option value="days">days</option>
                              <option value="weeks">weeks</option>
                              <option value="months">months</option>
                            </select>
                            <button
                              type="button"
                              class="btn-icon btn-edit-lt"
                              title="Edit leave type"
                              onClick={() => openEditLeaveTypeModal(policy)}
                              style={{ marginLeft: '0.25rem', padding: '0.25rem' }}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              class="btn-icon btn-delete-lt"
                              title="Delete leave type"
                              onClick={() => handleDeleteLeaveType(policy)}
                              style={{ padding: '0.25rem', color: '#ef4444' }}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      background: '#f8fafc',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      <p style={{ margin: '0 0 0.5rem 0' }}>No leave types configured yet</p>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        onClick={openAddLeaveTypeModal}
                      >
                        Add First Leave Type
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingDept ? 'Save Changes' : 'Create Department')}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Leave Type Modal */}
      {showLeaveTypeModal && (
        <>
          <div class="modal-overlay" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }} style={{ zIndex: 1001 }} />
          <div class="modal" style={{ zIndex: 1002, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleLeaveTypeSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
              <div class="modal-header" style={{ flexShrink: 0 }}>
                <h2>{editingLeaveType ? 'Edit' : 'Add'} Leave Type</h2>
                <button type="button" class="btn-icon" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div class="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={leaveTypeForm.name}
                    onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, name: e.target.value })}
                    required
                    placeholder="e.g., Vacation, Sick Leave"
                  />
                </div>
                <div class="form-row">
                  <div class="form-group">
                    <label>Icon</label>
                    <input
                      type="text"
                      class="form-input"
                      value={leaveTypeForm.icon}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, icon: e.target.value })}
                      placeholder="🏖️"
                    />
                  </div>
                  <div class="form-group">
                    <label>Colour</label>
                    <input
                      type="color"
                      class="form-input form-color"
                      value={leaveTypeForm.color}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, color: e.target.value })}
                    />
                  </div>
                </div>
                <div class="form-group">
                  <label>Track By</label>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="unit"
                        value="hours"
                        checked={leaveTypeForm.unit === 'hours'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'hours' })}
                      />
                      <span>Hours</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="unit"
                        value="days"
                        checked={leaveTypeForm.unit === 'days'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'days' })}
                      />
                      <span>Days</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="unit"
                        value="weeks"
                        checked={leaveTypeForm.unit === 'weeks'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'weeks' })}
                      />
                      <span>Weeks</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="unit"
                        value="months"
                        checked={leaveTypeForm.unit === 'months'}
                        onChange={() => setLeaveTypeForm({ ...leaveTypeForm, unit: 'months' })}
                      />
                      <span>Months</span>
                    </label>
                  </div>
                  <p class="form-hint">Choose the unit for tracking this leave type</p>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.requires_approval}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, requires_approval: e.target.checked })}
                    />
                    Requires Approval
                  </label>
                </div>
                {leaveTypeForm.unit === 'days' && (
                  <div class="form-group">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        checked={leaveTypeForm.allows_half_day}
                        onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, allows_half_day: e.target.checked })}
                      />
                      Allows Half Day
                    </label>
                  </div>
                )}
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={leaveTypeForm.is_paid}
                      onChange={(e) => setLeaveTypeForm({ ...leaveTypeForm, is_paid: e.target.checked })}
                    />
                    Paid Leave
                  </label>
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => { setShowLeaveTypeModal(false); setEditingLeaveType(null); }}>Cancel</button>
                <button type="submit" class="btn btn-primary">{editingLeaveType ? 'Update' : 'Create'} Leave Type</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function LocationsTab({ locations, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country_code: '',
    timezone: 'Europe/Malta',
    is_default: false
  });

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await createLocation(formData);
      setShowModal(false);
      setFormData({ name: '', country_code: '', timezone: 'Europe/Malta', is_default: false });
      onRefresh();
    } catch (err) {
      alert('Failed to create location');
    }
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Locations</h2>
          <p>Configure office locations with their timezones and holidays</p>
        </div>
        <button class="btn btn-primary" onClick={() => setShowModal(true)}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Location
        </button>
      </div>

      <div class="settings-grid">
        {locations.length > 0 ? (
          locations.map(loc => (
            <div key={loc.id} class={`settings-card ${loc.is_default ? 'default' : ''}`}>
              <div class="settings-card-header">
                <div class="location-icon">
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div class="settings-card-title">
                  <h3>{loc.name}</h3>
                  {loc.is_default && <span class="badge badge-approved">Default</span>}
                </div>
              </div>
              <div class="settings-card-body">
                <div class="detail-row">
                  <span class="detail-label">Country</span>
                  <span class="detail-value">{loc.country_code || 'Not set'}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Timezone</span>
                  <span class="detail-value">{loc.timezone}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Members</span>
                  <span class="detail-value">{loc.user_count || 0} users</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div class="empty-card">
            <div class="empty-state-icon">📍</div>
            <p>No locations configured yet</p>
          </div>
        )}
      </div>

      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
              <div class="modal-header" style={{ flexShrink: 0 }}>
                <h2>Add Location</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div class="form-group">
                  <label>Location Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Malta Office, London HQ"
                  />
                </div>
                <div class="form-group">
                  <label>Country Code</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., MT, GB, US"
                    maxLength="2"
                  />
                </div>
                <div class="form-group">
                  <label>Timezone</label>
                  <select
                    class="form-select"
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  >
                    <option value="Europe/Malta">Europe/Malta</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Rome">Europe/Rome</option>
                    <option value="America/New_York">America/New_York</option>
                    <option value="America/Los_Angeles">America/Los_Angeles</option>
                    <option value="Asia/Singapore">Asia/Singapore</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    Set as Default Location
                  </label>
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary">Create Location</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function HolidaysTab({ holidays, locations, onRefresh }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    location_id: '',
    recurring: true
  });
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [nextYearHolidays, setNextYearHolidays] = useState([]);
  const [loadingNextYear, setLoadingNextYear] = useState(false);

  // Load next year holidays when tab is selected
  useEffect(() => {
    if (selectedYear === nextYear && nextYearHolidays.length === 0 && !loadingNextYear) {
      setLoadingNextYear(true);
      getHolidays(nextYear)
        .then(data => setNextYearHolidays(data || []))
        .catch(err => console.error('Failed to load next year holidays:', err))
        .finally(() => setLoadingNextYear(false));
    }
  }, [selectedYear, nextYear]);

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      await createHoliday(formData);
      setShowModal(false);
      setFormData({ name: '', date: '', location_id: '', recurring: true });
      onRefresh();
      // Also reload next year if needed
      if (new Date(formData.date).getFullYear() === nextYear) {
        const data = await getHolidays(nextYear);
        setNextYearHolidays(data || []);
      }
    } catch (err) {
      alert('Failed to create holiday');
    }
  }

  async function handleDelete(holidayId) {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      await deleteHoliday(holidayId);
      onRefresh();
      // Also reload next year
      const data = await getHolidays(nextYear);
      setNextYearHolidays(data || []);
    } catch (err) {
      alert('Failed to delete holiday');
    }
  }

  // Get holidays for selected year
  const displayHolidays = selectedYear === currentYear ? holidays : nextYearHolidays;

  // Group holidays by month
  const holidaysByMonth = displayHolidays.reduce((acc, h) => {
    const month = new Date(h.date).toLocaleDateString('en-GB', { month: 'long' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(h);
    return acc;
  }, {});

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Public Holidays</h2>
          <p>Configure public holidays that affect leave calculations</p>
        </div>
        <button class="btn btn-primary" onClick={() => setShowModal(true)}>
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add Holiday
        </button>
      </div>

      {/* Year Tabs */}
      <div class="year-tabs" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button
          class={`btn ${selectedYear === currentYear ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSelectedYear(currentYear)}
        >
          {currentYear}
        </button>
        <button
          class={`btn ${selectedYear === nextYear ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSelectedYear(nextYear)}
        >
          {nextYear}
        </button>
      </div>

      {loadingNextYear && selectedYear === nextYear ? (
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      ) : Object.keys(holidaysByMonth).length > 0 ? (
        <div class="holidays-list">
          {Object.entries(holidaysByMonth).map(([month, monthHolidays]) => (
            <div key={month} class="holiday-month-group">
              <h3 class="month-header">{month}</h3>
              <div class="holiday-cards">
                {monthHolidays.map(holiday => (
                  <div key={holiday.id} class="holiday-card">
                    <div class="holiday-date">
                      <span class="day">{new Date(holiday.date).getDate()}</span>
                      <span class="weekday">{new Date(holiday.date).toLocaleDateString('en-GB', { weekday: 'short' })}</span>
                    </div>
                    <div class="holiday-info">
                      <h4>{holiday.name}</h4>
                      <span class="holiday-location">
                        {holiday.location_name || 'All Locations'}
                        {holiday.recurring && <span class="recurring-badge">Recurring</span>}
                      </span>
                    </div>
                    <button class="btn-icon btn-danger" onClick={() => handleDelete(holiday.id)}>
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div class="empty-card full-width">
          <div class="empty-state-icon">🎉</div>
          <h3>No holidays configured for {selectedYear}</h3>
          <p>Add public holidays for accurate leave calculations</p>
          <button class="btn btn-primary" onClick={() => setShowModal(true)}>Add First Holiday</button>
        </div>
      )}

      {showModal && (
        <>
          <div class="modal-overlay" onClick={() => setShowModal(false)} />
          <div class="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden' }}>
              <div class="modal-header" style={{ flexShrink: 0 }}>
                <h2>Add Holiday</h2>
                <button type="button" class="btn-icon" onClick={() => setShowModal(false)}>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div class="modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div class="form-group">
                  <label>Holiday Name</label>
                  <input
                    type="text"
                    class="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Christmas Day, New Year's Day"
                  />
                </div>
                <div class="form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    class="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div class="form-group">
                  <label>Location</label>
                  <select
                    class="form-select"
                    value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  >
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div class="form-group">
                  <label class="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.recurring}
                      onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                    />
                    Recurring Annually
                  </label>
                </div>
              </div>
              <div class="modal-footer" style={{ flexShrink: 0, background: 'white' }}>
                <button type="button" class="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" class="btn btn-primary">Create Holiday</button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

function NotificationsTab({ departments }) {
  const [settings, setSettings] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [settingsData, channelsData] = await Promise.all([
        getNotificationSettings(),
        getSlackChannels().catch(() => [])
      ]);
      setSettings(settingsData);
      setChannels(channelsData || []);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      await updateNotificationSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  }

  function updateSetting(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Team Notifications</h2>
          <p>Configure when and how your team receives notifications</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div class="notification-settings">
        <div class="card">
          <div class="card-header">
            <h3>Daily Summary</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.daily_summary}
                  onChange={(e) => updateSetting('daily_summary', e.target.checked)}
                />
                Send daily summary to Slack channel
              </label>
              <p class="form-hint">Get a daily overview of who's off today and upcoming leaves</p>
            </div>
            <div class="form-group">
              <label>Summary Time</label>
              <input
                type="time"
                class="form-input"
                value={settings?.daily_summary_time || '09:00'}
                onChange={(e) => updateSetting('daily_summary_time', e.target.value)}
                style="max-width: 150px;"
              />
            </div>
            <div class="form-group">
              <label>Slack Channel</label>
              <select
                class="form-select"
                value={settings?.slack_channel_id || ''}
                onChange={(e) => updateSetting('slack_channel_id', e.target.value)}
                style="max-width: 300px;"
              >
                <option value="">Select a channel...</option>
                {channels.map(ch => (
                  <option key={ch.id} value={ch.id}>
                    {ch.is_private ? '🔒 ' : '#'}{ch.name}
                  </option>
                ))}
              </select>
              {channels.length === 0 && (
                <p class="form-hint">No channels found. Make sure the bot is added to channels.</p>
              )}
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3>Weekly Summary</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.weekly_summary}
                  onChange={(e) => updateSetting('weekly_summary', e.target.checked)}
                />
                Send weekly summary
              </label>
              <p class="form-hint">Get a weekly digest of team availability</p>
            </div>
            <div class="form-group">
              <label>Day of Week</label>
              <select
                class="form-select"
                value={settings?.weekly_summary_day || 1}
                onChange={(e) => updateSetting('weekly_summary_day', parseInt(e.target.value))}
                style="max-width: 200px;"
              >
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
              </select>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3>Event Notifications</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_request}
                  onChange={(e) => updateSetting('notify_on_request', e.target.checked)}
                />
                Notify when leave is requested
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_approval}
                  onChange={(e) => updateSetting('notify_on_approval', e.target.checked)}
                />
                Notify when leave is approved
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_on_rejection}
                  onChange={(e) => updateSetting('notify_on_rejection', e.target.checked)}
                />
                Notify when leave is rejected
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_upcoming_leaves}
                  onChange={(e) => updateSetting('notify_upcoming_leaves', e.target.checked)}
                />
                Notify about upcoming leaves (1 day before)
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_holidays}
                  onChange={(e) => updateSetting('notify_holidays', e.target.checked)}
                />
                Notify about upcoming public holidays
              </label>
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings?.notify_birthdays}
                  onChange={(e) => updateSetting('notify_birthdays', e.target.checked)}
                />
                Notify about team member birthdays
              </label>
            </div>
          </div>
        </div>

        <button class="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>
    </div>
  );
}

function WorkspaceTab({ workspace }) {
  const [settings, setSettings] = useState({
    max_carryover_days: 4,
    week_starts_on: 1,
    leave_year_start_month: 1,
    date_format: 'DD/MM/YYYY'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const data = await getWorkspaceSettings();
      setSettings({
        max_carryover_days: data.max_carryover_days ?? 4,
        week_starts_on: data.week_starts_on ?? 1,
        leave_year_start_month: data.leave_year_start_month ?? 1,
        date_format: data.date_format ?? 'DD/MM/YYYY'
      });
    } catch (error) {
      console.error('Failed to load workspace settings:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      await updateWorkspaceSettings(settings);
      setMessage({ type: 'success', text: 'Workspace settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings: ' + error.message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Workspace Settings</h2>
          <p>Configure your workspace preferences and leave policies</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div class="card">
        <div class="card-header">
          <h3>General Settings</h3>
        </div>
        <div class="card-body">
          <div style="max-width: 500px;">
            <div class="form-group">
              <label>Workspace Name</label>
              <input
                type="text"
                class="form-input"
                value={workspace?.name || ''}
                disabled
              />
            </div>

            <div class="form-group">
              <label>Plan</label>
              <div class="plan-display">
                <span class="plan-name">{workspace?.plan || 'Free'}</span>
                <a href="#" class="upgrade-link">Upgrade Plan</a>
              </div>
            </div>

            <div class="form-group">
              <label>Week Starts On</label>
              <select
                class="form-select"
                value={settings.week_starts_on}
                onChange={(e) => setSettings({ ...settings, week_starts_on: parseInt(e.target.value) })}
              >
                <option value="1">Monday</option>
                <option value="0">Sunday</option>
              </select>
            </div>

            <div class="form-group">
              <label>Leave Year Starts</label>
              <select
                class="form-select"
                value={settings.leave_year_start_month}
                onChange={(e) => setSettings({ ...settings, leave_year_start_month: parseInt(e.target.value) })}
              >
                <option value="1">January</option>
                <option value="4">April</option>
                <option value="7">July</option>
                <option value="10">October</option>
              </select>
            </div>

            <div class="form-group">
              <label>Date Format</label>
              <select
                class="form-select"
                value={settings.date_format}
                onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="card" style={{ marginTop: '1.5rem' }}>
        <div class="card-header">
          <h3>Leave Carryover Policy</h3>
        </div>
        <div class="card-body">
          <div style="max-width: 500px;">
            <div class="form-group">
              <label>Maximum Carryover Days</label>
              <input
                type="number"
                class="form-input"
                min="0"
                max="30"
                value={settings.max_carryover_days}
                onChange={(e) => setSettings({ ...settings, max_carryover_days: parseInt(e.target.value) || 0 })}
                style="max-width: 120px;"
              />
              <p class="form-hint">
                Maximum days of unused vacation leave that can be carried over to the next year.
                Malta's statutory limit is 4 days.
              </p>
            </div>

            <div class="info-box" style={{
              backgroundColor: 'var(--color-info-bg, #e0f2fe)',
              border: '1px solid var(--color-info-border, #7dd3fc)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
                How carryover works:
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
                <li>On January 1st, unused vacation days (up to {settings.max_carryover_days} days) are automatically carried over</li>
                <li>Carried over days must be used by March 31st or they expire</li>
                <li>Set to 0 to disable carryover entirely</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <button
        class="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: '1.5rem' }}
      >
        {saving ? 'Saving...' : 'Save Workspace Settings'}
      </button>
    </div>
  );
}

function OpeningBalancesTab({ allUsers, leaveTypes }) {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [editValue, setEditValue] = useState('');
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadBalances();
  }, [selectedYear]);

  async function loadBalances() {
    setLoading(true);
    try {
      const data = await getAllUserBalances(selectedYear);
      setBalances(data || []);
    } catch (error) {
      console.error('Failed to load balances:', error);
      setMessage({ type: 'error', text: 'Failed to load balances' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveBalance(userId, leaveTypeId, field, value) {
    setSaving(`${userId}-${leaveTypeId}`);
    setMessage(null);
    try {
      await updateUserBalance(userId, leaveTypeId, selectedYear, { [field]: parseFloat(value) || 0 });
      setMessage({ type: 'success', text: 'Balance updated successfully!' });
      setEditingBalance(null);
      loadBalances();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update balance: ' + error.message });
    } finally {
      setSaving(null);
    }
  }

  function startEditing(userId, leaveTypeId, field, currentValue) {
    setEditingBalance(`${userId}-${leaveTypeId}-${field}`);
    setEditValue(currentValue?.toString() || '0');
  }

  function cancelEditing() {
    setEditingBalance(null);
    setEditValue('');
  }

  // Group balances by user
  const balancesByUser = {};
  balances.forEach(b => {
    if (!balancesByUser[b.user_id]) {
      balancesByUser[b.user_id] = {
        user_id: b.user_id,
        user_name: b.user_name,
        avatar_url: b.avatar_url,
        department_name: b.department_name,
        balances: {}
      };
    }
    balancesByUser[b.user_id].balances[b.leave_type_id] = b;
  });

  // Filter users by search query
  const filteredUsers = Object.values(balancesByUser).filter(u =>
    !searchQuery || u.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get active users who don't have balances yet
  const usersWithBalances = Object.keys(balancesByUser);
  const usersWithoutBalances = allUsers.filter(u =>
    u.is_active && !usersWithBalances.includes(u.id) &&
    (!searchQuery || u.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Opening Balances</h2>
          <p>Set opening balances for employees who joined mid-year or to adjust balances</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div class="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Year</label>
          <select
            class="form-select"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ width: '120px' }}
          >
            <option value={currentYear - 1}>{currentYear - 1}</option>
            <option value={currentYear}>{currentYear}</option>
            <option value={currentYear + 1}>{currentYear + 1}</option>
          </select>
        </div>
        <div class="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px', maxWidth: '300px' }}>
          <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>Search</label>
          <input
            type="text"
            class="form-input"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div class="card">
        <div class="card-body" style={{ padding: 0, overflowX: 'auto' }}>
          <table class="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>Employee</th>
                {leaveTypes.filter(lt => lt.is_active !== false).map(lt => (
                  <th key={lt.id} style={{ padding: '0.75rem 1rem', textAlign: 'center', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', minWidth: '150px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                      {lt.icon} {lt.name}
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>({lt.unit === 'hours' ? 'hrs' : 'days'})</span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <tr key={user.user_id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div class="avatar avatar-sm">
                          {user.avatar_url ? <img src={user.avatar_url} alt="" /> : user.user_name?.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.user_name}</div>
                          {user.department_name && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{user.department_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => {
                      const balance = user.balances[lt.id];
                      const editKey = `${user.user_id}-${lt.id}-carried_over`;
                      const isEditing = editingBalance === editKey;
                      const isSaving = saving === `${user.user_id}-${lt.id}`;
                      const unitLabel = lt.unit === 'hours' ? 'hrs' : 'days';

                      return (
                        <td key={lt.id} style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                          {balance ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Entitlement: <strong>{balance.entitlement || 0}</strong> <span style={{ fontSize: '0.75rem' }}>{unitLabel}</span>
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                Used: <strong>{balance.used || 0}</strong> <span style={{ fontSize: '0.75rem' }}>{unitLabel}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.875rem', color: '#059669' }}>Opening:</span>
                                {isEditing ? (
                                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                    <input
                                      type="number"
                                      class="form-input"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      style={{ width: '70px', padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
                                      step={lt.unit === 'hours' ? '0.5' : '0.5'}
                                      min="0"
                                      autoFocus
                                    />
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{unitLabel}</span>
                                    <button
                                      class="btn-icon"
                                      onClick={() => handleSaveBalance(user.user_id, lt.id, 'carried_over', editValue)}
                                      disabled={isSaving}
                                      style={{ color: '#059669' }}
                                    >
                                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                      </svg>
                                    </button>
                                    <button
                                      class="btn-icon"
                                      onClick={cancelEditing}
                                      disabled={isSaving}
                                      style={{ color: '#ef4444' }}
                                    >
                                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    class="balance-edit-btn"
                                    onClick={() => startEditing(user.user_id, lt.id, 'carried_over', balance.carried_over)}
                                    style={{
                                      background: 'none',
                                      border: '1px dashed #d1d5db',
                                      borderRadius: '4px',
                                      padding: '0.125rem 0.5rem',
                                      cursor: 'pointer',
                                      fontWeight: '500',
                                      color: '#059669'
                                    }}
                                  >
                                    {balance.carried_over || 0} <span style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>{unitLabel}</span>
                                  </button>
                                )}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                                Available: <strong style={{ color: '#3b82f6' }}>{(balance.entitlement || 0) + (balance.carried_over || 0) + (balance.adjustment || 0) - (balance.used || 0)}</strong> {unitLabel}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No balance</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : usersWithoutBalances.length > 0 ? (
                usersWithoutBalances.slice(0, 10).map(user => (
                  <tr key={user.id}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div class="avatar avatar-sm">
                          {user.avatar_url ? <img src={user.avatar_url} alt="" /> : user.name?.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>No balances set</div>
                        </div>
                      </div>
                    </td>
                    {leaveTypes.filter(lt => lt.is_active !== false).map(lt => (
                      <td key={lt.id} style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={leaveTypes.length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div class="info-box" style={{
        backgroundColor: '#e0f2fe',
        border: '1px solid #7dd3fc',
        borderRadius: '8px',
        padding: '1rem',
        marginTop: '1.5rem'
      }}>
        <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
          About Opening Balances:
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 1.6 }}>
          <li><strong>Opening Balance</strong> is added to the employee's entitlement</li>
          <li>Use this to set carried over days from previous years</li>
          <li>Or to adjust balances for employees who joined mid-year</li>
          <li>Click on the number to edit the opening balance</li>
        </ul>
      </div>
    </div>
  );
}

function DataManagementTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const data = await getDataStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load data stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadBackup() {
    setProcessing(true);
    setMessage(null);
    try {
      await downloadBackup();
      setMessage({ type: 'success', text: 'Backup downloaded successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to download backup: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  async function handleClearUsers() {
    setProcessing(true);
    setMessage(null);
    setConfirmAction(null);
    try {
      const result = await clearNonAdminUsers();
      setMessage({ type: 'success', text: `Removed ${result.deletedCount} non-admin users` });
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear users: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  async function handleClearHistory() {
    setProcessing(true);
    setMessage(null);
    setConfirmAction(null);
    try {
      const result = await clearLeaveHistory();
      setMessage({ type: 'success', text: `Cleared ${result.deletedLeaveRequests} leave requests and reset ${result.resetBalances} balances` });
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear history: ' + error.message });
    } finally {
      setProcessing(false);
    }
  }

  if (loading) {
    return (
      <div class="settings-section">
        <div class="loading-screen" style="min-height: 200px;">
          <div class="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div class="settings-section">
      <div class="settings-section-header">
        <div>
          <h2>Data Management</h2>
          <p>Backup your data or clear workspace information</p>
        </div>
      </div>

      {message && (
        <div class={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      {/* Data Statistics */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>Current Data</h3>
        </div>
        <div class="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-primary, #3b82f6)' }}>{stats?.users?.total || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Total Users</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-warning, #f59e0b)' }}>{stats?.users?.admins || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Admins/Owners</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-success, #10b981)' }}>{stats?.users?.regular || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Regular Users</div>
            </div>
            <div class="stat-box" style={{ padding: '1rem', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '600', color: 'var(--color-info, #6366f1)' }}>{stats?.leaveRequests?.total || 0}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary, #6b7280)' }}>Leave Requests</div>
            </div>
          </div>
        </div>
      </div>

      {/* Backup */}
      <div class="card" style={{ marginBottom: '1.5rem' }}>
        <div class="card-header">
          <h3>Backup Data</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Download a complete backup of all workspace data including users, leave types, departments, locations, holidays, leave requests, and balances.
          </p>
          <button
            class="btn btn-primary"
            onClick={handleDownloadBackup}
            disabled={processing}
          >
            {processing ? 'Downloading...' : 'Download Backup'}
          </button>
        </div>
      </div>

      {/* Clear Users */}
      <div class="card" style={{ marginBottom: '1.5rem', borderColor: 'var(--color-danger, #ef4444)' }}>
        <div class="card-header" style={{ borderBottomColor: 'var(--color-danger-light, #fecaca)' }}>
          <h3 style={{ color: 'var(--color-danger, #ef4444)' }}>Clear Non-Admin Users</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Remove all users except admins and owners. This will delete {stats?.users?.regular || 0} users, their leave balances, and any pending leave requests.
          </p>
          <div class="info-box" style={{
            backgroundColor: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-light, #fecaca)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Warning:</strong> This action cannot be undone. Make sure to download a backup first.
          </div>
          {confirmAction === 'users' ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-danger, #ef4444)', fontWeight: '500' }}>Are you sure?</span>
              <button
                class="btn btn-danger"
                onClick={handleClearUsers}
                disabled={processing}
              >
                {processing ? 'Clearing...' : 'Yes, Delete Users'}
              </button>
              <button
                class="btn btn-secondary"
                onClick={() => setConfirmAction(null)}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              class="btn btn-danger-outline"
              onClick={() => setConfirmAction('users')}
              disabled={processing || stats?.users?.regular === 0}
            >
              Clear Non-Admin Users
            </button>
          )}
        </div>
      </div>

      {/* Clear Leave History */}
      <div class="card" style={{ borderColor: 'var(--color-danger, #ef4444)' }}>
        <div class="card-header" style={{ borderBottomColor: 'var(--color-danger-light, #fecaca)' }}>
          <h3 style={{ color: 'var(--color-danger, #ef4444)' }}>Clear Leave History</h3>
        </div>
        <div class="card-body">
          <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary, #6b7280)' }}>
            Remove all leave requests and reset all leave balances to zero. This will delete {stats?.leaveRequests?.total || 0} leave requests.
          </p>
          <div class="info-box" style={{
            backgroundColor: 'var(--color-danger-bg, #fef2f2)',
            border: '1px solid var(--color-danger-light, #fecaca)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <strong>Warning:</strong> This action cannot be undone. All leave history will be permanently deleted and balances reset.
          </div>
          {confirmAction === 'history' ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--color-danger, #ef4444)', fontWeight: '500' }}>Are you sure?</span>
              <button
                class="btn btn-danger"
                onClick={handleClearHistory}
                disabled={processing}
              >
                {processing ? 'Clearing...' : 'Yes, Clear History'}
              </button>
              <button
                class="btn btn-secondary"
                onClick={() => setConfirmAction(null)}
                disabled={processing}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              class="btn btn-danger-outline"
              onClick={() => setConfirmAction('history')}
              disabled={processing || stats?.leaveRequests?.total === 0}
            >
              Clear Leave History
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
